import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { onboardingAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, AlertCircle, CheckCircle, Building2, User, Calendar as CalendarIcon, FileCheck } from 'lucide-react';
import { toast } from 'sonner';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_2c2cfe83-306e-4fb5-b370-367ad0387383/artifacts/k0uivc6c_4skr1hVuGATobkW3-light.png";

// Helper to convert DD/MM/YYYY to YYYY-MM-DD for API
const convertDateForAPI = (dateStr) => {
    if (!dateStr || dateStr.length !== 10) return dateStr;
    const parts = dateStr.split('/');
    if (parts.length !== 3) return dateStr;
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
};

const ENTITY_TYPES = [
    'Individual',
    'Sole Trader',
    'Non-Registered Community Organisation',
    'Private Company Limited by Shares (LTD)',
    'Designated Activity Company (DAC)',
    'Company Limited by Guarantee (CLG)',
    'Owners Management Company (OMC)',
    'Public Limited Company (PLC)',
    'Unlimited Company (ULC)',
    'Societas Europaea Company (SE)'
];

const GENDERS = [
    { value: 'Male', label: 'Male' },
    { value: 'Female', label: 'Female' }
];

const NATIONALITIES = [
    { code: 'IE', name: 'Irish' },
    { code: 'GB', name: 'British' },
    { code: 'US', name: 'American' },
    { code: 'DE', name: 'German' },
    { code: 'FR', name: 'French' },
    { code: 'ES', name: 'Spanish' },
    { code: 'IT', name: 'Italian' },
    { code: 'PL', name: 'Polish' },
    { code: 'PT', name: 'Portuguese' },
    { code: 'NL', name: 'Dutch' },
    { code: 'BE', name: 'Belgian' },
    { code: 'AT', name: 'Austrian' },
    { code: 'SE', name: 'Swedish' },
    { code: 'DK', name: 'Danish' },
    { code: 'FI', name: 'Finnish' },
    { code: 'NO', name: 'Norwegian' },
    { code: 'CH', name: 'Swiss' },
    { code: 'AU', name: 'Australian' },
    { code: 'CA', name: 'Canadian' },
    { code: 'NZ', name: 'New Zealander' },
    { code: 'OTHER', name: 'Other' }
];

const COUNTIES = [
    'Antrim', 'Armagh', 'Carlow', 'Cavan', 'Clare', 'Cork', 'Derry', 'Donegal',
    'Down', 'Dublin', 'Fermanagh', 'Galway', 'Kerry', 'Kildare', 'Kilkenny',
    'Laois', 'Leitrim', 'Limerick', 'Longford', 'Louth', 'Mayo', 'Meath',
    'Monaghan', 'Offaly', 'Roscommon', 'Sligo', 'Tipperary', 'Tyrone',
    'Waterford', 'Westmeath', 'Wexford', 'Wicklow'
];

const EVENT_TYPES = [
    { id: 'theatre', label: 'Theatre Productions', icon: '🎭' },
    { id: 'music', label: 'Live Music', icon: '🎤' },
    { id: 'dance', label: 'Dance Performances', icon: '🩰' },
    { id: 'film', label: 'Film Screenings', icon: '🎬' },
    { id: 'arts', label: 'Arts & Culture', icon: '🎨' },
    { id: 'workshops', label: 'Workshops & Classes', icon: '📚' },
    { id: 'talks', label: 'Talks & Conferences', icon: '🎙️' },
    { id: 'markets', label: 'Markets & Fairs', icon: '🛍️' },
    { id: 'family', label: 'Family & Community', icon: '👨‍👩‍👧' },
    { id: 'college', label: 'College & University', icon: '🎓' },
    { id: 'fundraisers', label: 'Fundraisers & Charity', icon: '💝' },
    { id: 'social', label: 'Social Events & Parties', icon: '🎉' },
    { id: 'gaming', label: 'Gaming & eSports', icon: '🎮' },
    { id: 'religious', label: 'Religious Events', icon: '⛪' },
    { id: 'sports', label: 'Sports Events', icon: '🏅' },
    { id: 'other', label: 'Other', icon: '🎟️' }
];

const ATTENDANCE_OPTIONS = [
    'Less than 25',
    '25-50',
    '51-100',
    '100+'
];

const PLANNED_EVENTS_OPTIONS = [
    'One Event',
    '2-5',
    '6-10',
    '11-25',
    '26+',
    "I don't know"
];

const STEPS = [
    { id: 'organization', label: 'Organisation', icon: Building2 },
    { id: 'personal', label: 'Personal', icon: User },
    { id: 'events', label: 'Events', icon: CalendarIcon },
    { id: 'consent', label: 'Confirm', icon: FileCheck }
];

export default function Onboarding() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [currentStep, setCurrentStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    
    const [formData, setFormData] = useState({
        organization: {
            organisation_name: '',
            entity_type: '',
            // Business-specific fields
            registration_number: '',  // CRO number
            vat_number: ''
        },
        personal: {
            first_name: user?.first_name || '',
            surname: user?.last_name || '',
            // Individual-specific fields
            date_of_birth: '',
            gender: '',
            nationality: 'IE',
            // Address
            address_1: '',
            address_2: '',
            town_city: '',
            county: '',
            eircode: '',
            mobile_phone: '',
            mobile_country_code: '+353',
            email: user?.email || ''
        },
        event_details: {
            event_types: [],
            average_attendance: '',
            planned_events: '',
            how_heard: ''
        },
        consent: {
            payment_consent: false,
            marketing_consent: false,
            promo_code: ''
        }
    });

    // Check if entity type is individual/sole trader
    const isIndividual = ['Individual', 'Sole Trader'].includes(formData.organization.entity_type);
    const isBusiness = formData.organization.entity_type && !isIndividual;

    const updateFormData = (section, field, value) => {
        setFormData(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: value
            }
        }));
        setError('');
    };

    const toggleEventType = (eventId) => {
        const current = formData.event_details.event_types;
        const updated = current.includes(eventId)
            ? current.filter(id => id !== eventId)
            : [...current, eventId];
        updateFormData('event_details', 'event_types', updated);
    };

    const validateStep = () => {
        switch (currentStep) {
            case 0:
                if (!formData.organization.organisation_name || !formData.organization.entity_type) {
                    setError('Please fill in all required fields');
                    return false;
                }
                // Validate business-specific fields
                if (isBusiness && !formData.organization.registration_number) {
                    setError('Company Registration Number is required for business entities');
                    return false;
                }
                break;
            case 1:
                const p = formData.personal;
                if (!p.first_name || !p.surname || !p.address_1 || !p.town_city || !p.county || !p.eircode || !p.mobile_phone || !p.email) {
                    setError('Please fill in all required fields');
                    return false;
                }
                // Validate individual-specific fields
                if (isIndividual) {
                    if (!p.date_of_birth || !p.gender || !p.nationality) {
                        setError('Date of birth, gender, and nationality are required for individual accounts');
                        return false;
                    }
                }
                break;
            case 2:
                const e = formData.event_details;
                if (e.event_types.length === 0 || !e.average_attendance || !e.planned_events || !e.how_heard) {
                    setError('Please fill in all required fields');
                    return false;
                }
                break;
            case 3:
                if (!formData.consent.payment_consent) {
                    setError('You must accept the payment authorisation to continue');
                    return false;
                }
                break;
            default:
                break;
        }
        return true;
    };

    const handleNext = () => {
        if (validateStep()) {
            setCurrentStep(prev => Math.min(prev + 1, STEPS.length - 1));
        }
    };

    const handleBack = () => {
        setCurrentStep(prev => Math.max(prev - 1, 0));
    };

    const handleSubmit = async () => {
        if (!validateStep()) return;

        setLoading(true);
        setError('');

        try {
            // Convert date format before submission
            const submissionData = {
                ...formData,
                personal: {
                    ...formData.personal,
                    date_of_birth: convertDateForAPI(formData.personal.date_of_birth)
                }
            };
            await onboardingAPI.submit(submissionData);
            toast.success('Application submitted successfully!');
            navigate('/dashboard');
        } catch (err) {
            const message = err.response?.data?.detail || 'Failed to submit application';
            setError(message);
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case 0:
                return (
                    <div className="space-y-6">
                        <div className="space-y-2">
                            <Label htmlFor="organisation_name">Organisation Name *</Label>
                            <Input
                                id="organisation_name"
                                placeholder="Your organisation or trading name"
                                value={formData.organization.organisation_name}
                                onChange={(e) => updateFormData('organization', 'organisation_name', e.target.value)}
                                className="bg-muted/50"
                                data-testid="org-name-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="entity_type">Entity Type *</Label>
                            <Select
                                value={formData.organization.entity_type}
                                onValueChange={(value) => updateFormData('organization', 'entity_type', value)}
                            >
                                <SelectTrigger className="bg-muted/50" data-testid="entity-type-select">
                                    <SelectValue placeholder="Select entity type" />
                                </SelectTrigger>
                                <SelectContent>
                                    {ENTITY_TYPES.map((type) => (
                                        <SelectItem key={type} value={type}>{type}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        
                        {/* Business-specific fields */}
                        {isBusiness && (
                            <>
                                <div className="space-y-2">
                                    <Label htmlFor="registration_number">Company Registration Number (CRO) *</Label>
                                    <Input
                                        id="registration_number"
                                        placeholder="e.g. 123456"
                                        value={formData.organization.registration_number}
                                        onChange={(e) => updateFormData('organization', 'registration_number', e.target.value)}
                                        className="bg-muted/50"
                                        data-testid="registration-number-input"
                                    />
                                    <p className="text-xs text-muted-foreground">
                                        Your Companies Registration Office number
                                    </p>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="vat_number">VAT Number (optional)</Label>
                                    <Input
                                        id="vat_number"
                                        placeholder="e.g. IE1234567A"
                                        value={formData.organization.vat_number}
                                        onChange={(e) => updateFormData('organization', 'vat_number', e.target.value)}
                                        className="bg-muted/50"
                                        data-testid="vat-number-input"
                                    />
                                </div>
                            </>
                        )}
                    </div>
                );
            
            case 1:
                return (
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="first_name">First Name *</Label>
                                <Input
                                    id="first_name"
                                    value={formData.personal.first_name}
                                    onChange={(e) => updateFormData('personal', 'first_name', e.target.value)}
                                    className="bg-muted/50"
                                    data-testid="personal-firstname-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="surname">Surname *</Label>
                                <Input
                                    id="surname"
                                    value={formData.personal.surname}
                                    onChange={(e) => updateFormData('personal', 'surname', e.target.value)}
                                    className="bg-muted/50"
                                    data-testid="personal-surname-input"
                                />
                            </div>
                        </div>
                        
                        {/* Individual-specific fields */}
                        {isIndividual && (
                            <>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="date_of_birth">Date of Birth *</Label>
                                        <Input
                                            id="date_of_birth"
                                            placeholder="DD/MM/YYYY"
                                            value={formData.personal.date_of_birth}
                                            onChange={(e) => {
                                                let value = e.target.value.replace(/[^\d/]/g, '');
                                                // Auto-add slashes
                                                if (value.length === 2 && !value.includes('/')) {
                                                    value = value + '/';
                                                } else if (value.length === 5 && value.split('/').length === 2) {
                                                    value = value + '/';
                                                }
                                                // Limit to 10 characters (DD/MM/YYYY)
                                                if (value.length <= 10) {
                                                    updateFormData('personal', 'date_of_birth', value);
                                                }
                                            }}
                                            className="bg-muted/50"
                                            data-testid="personal-dob-input"
                                        />
                                        <p className="text-xs text-muted-foreground">Format: DD/MM/YYYY</p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="gender">Gender *</Label>
                                        <Select
                                            value={formData.personal.gender}
                                            onValueChange={(value) => updateFormData('personal', 'gender', value)}
                                        >
                                            <SelectTrigger className="bg-muted/50" data-testid="personal-gender-select">
                                                <SelectValue placeholder="Select gender" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {GENDERS.map((g) => (
                                                    <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="nationality">Nationality *</Label>
                                    <Select
                                        value={formData.personal.nationality}
                                        onValueChange={(value) => updateFormData('personal', 'nationality', value)}
                                    >
                                        <SelectTrigger className="bg-muted/50" data-testid="personal-nationality-select">
                                            <SelectValue placeholder="Select nationality" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {NATIONALITIES.map((n) => (
                                                <SelectItem key={n.code} value={n.code}>{n.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </>
                        )}
                        
                        <div className="space-y-2">
                            <Label htmlFor="address_1">Address Line 1 *</Label>
                            <Input
                                id="address_1"
                                value={formData.personal.address_1}
                                onChange={(e) => updateFormData('personal', 'address_1', e.target.value)}
                                className="bg-muted/50"
                                data-testid="personal-address1-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="address_2">Address Line 2</Label>
                            <Input
                                id="address_2"
                                value={formData.personal.address_2}
                                onChange={(e) => updateFormData('personal', 'address_2', e.target.value)}
                                className="bg-muted/50"
                                data-testid="personal-address2-input"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="town_city">Town/City *</Label>
                                <Input
                                    id="town_city"
                                    value={formData.personal.town_city}
                                    onChange={(e) => updateFormData('personal', 'town_city', e.target.value)}
                                    className="bg-muted/50"
                                    data-testid="personal-city-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="county">County *</Label>
                                <Select
                                    value={formData.personal.county}
                                    onValueChange={(value) => updateFormData('personal', 'county', value)}
                                >
                                    <SelectTrigger className="bg-muted/50" data-testid="personal-county-select">
                                        <SelectValue placeholder="Select county" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {COUNTIES.map((county) => (
                                            <SelectItem key={county} value={county}>{county}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="eircode">Eircode *</Label>
                            <Input
                                id="eircode"
                                placeholder="e.g. D02 X285"
                                value={formData.personal.eircode}
                                onChange={(e) => updateFormData('personal', 'eircode', e.target.value.toUpperCase())}
                                className="bg-muted/50"
                                data-testid="personal-eircode-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="mobile_phone">Mobile Phone *</Label>
                            <div className="flex gap-2">
                                <Select
                                    value={formData.personal.mobile_country_code}
                                    onValueChange={(value) => updateFormData('personal', 'mobile_country_code', value)}
                                >
                                    <SelectTrigger className="w-24 bg-muted/50" data-testid="personal-country-code-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="+353">+353</SelectItem>
                                        <SelectItem value="+44">+44</SelectItem>
                                    </SelectContent>
                                </Select>
                                <Input
                                    id="mobile_phone"
                                    placeholder="8X XXX XXXX"
                                    value={formData.personal.mobile_phone}
                                    onChange={(e) => updateFormData('personal', 'mobile_phone', e.target.value)}
                                    className="flex-1 bg-muted/50"
                                    data-testid="personal-phone-input"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="email">Email *</Label>
                            <Input
                                id="email"
                                type="email"
                                value={formData.personal.email}
                                onChange={(e) => updateFormData('personal', 'email', e.target.value)}
                                className="bg-muted/50"
                                data-testid="personal-email-input"
                            />
                        </div>
                    </div>
                );

            case 2:
                return (
                    <div className="space-y-6">
                        <div className="space-y-3">
                            <Label>What type of events do you host? *</Label>
                            <div className="grid grid-cols-2 gap-2">
                                {EVENT_TYPES.map((event) => (
                                    <div
                                        key={event.id}
                                        onClick={() => toggleEventType(event.id)}
                                        className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                                            formData.event_details.event_types.includes(event.id)
                                                ? 'border-primary bg-primary/10'
                                                : 'border-border bg-muted/30 hover:border-muted-foreground'
                                        }`}
                                        data-testid={`event-type-${event.id}`}
                                    >
                                        <span className="text-sm">{event.icon} {event.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Average attendance per event? *</Label>
                            <Select
                                value={formData.event_details.average_attendance}
                                onValueChange={(value) => updateFormData('event_details', 'average_attendance', value)}
                            >
                                <SelectTrigger className="bg-muted/50" data-testid="attendance-select">
                                    <SelectValue placeholder="Select attendance range" />
                                </SelectTrigger>
                                <SelectContent>
                                    {ATTENDANCE_OPTIONS.map((opt) => (
                                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>How many events in the next year? *</Label>
                            <Select
                                value={formData.event_details.planned_events}
                                onValueChange={(value) => updateFormData('event_details', 'planned_events', value)}
                            >
                                <SelectTrigger className="bg-muted/50" data-testid="planned-events-select">
                                    <SelectValue placeholder="Select number of events" />
                                </SelectTrigger>
                                <SelectContent>
                                    {PLANNED_EVENTS_OPTIONS.map((opt) => (
                                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="how_heard">How did you hear about us? *</Label>
                            <Textarea
                                id="how_heard"
                                placeholder="Tell us how you found GeekTickets..."
                                value={formData.event_details.how_heard}
                                onChange={(e) => updateFormData('event_details', 'how_heard', e.target.value)}
                                className="bg-muted/50"
                                data-testid="how-heard-input"
                            />
                        </div>
                    </div>
                );

            case 3:
                return (
                    <div className="space-y-6">
                        <div className="p-4 rounded-lg bg-muted/30 border border-border space-y-4">
                            <div className="flex items-start gap-3">
                                <Checkbox
                                    id="payment_consent"
                                    checked={formData.consent.payment_consent}
                                    onCheckedChange={(checked) => updateFormData('consent', 'payment_consent', checked)}
                                    data-testid="payment-consent-checkbox"
                                />
                                <Label htmlFor="payment_consent" className="text-sm leading-relaxed cursor-pointer">
                                    I authorise GeekTickets to deposit the proceeds of my event ticket sales—less any applicable 
                                    GeekTickets fees—into the bank account I will provide during the RyftPay verification process, 
                                    once the event has concluded or in accordance with the agreed payout schedule. I also authorise 
                                    GeekTickets to debit this account, if necessary, in order to issue refunds to customers for 
                                    events that are cancelled or have undergone material changes.
                                </Label>
                            </div>
                            <div className="flex items-start gap-3">
                                <Checkbox
                                    id="marketing_consent"
                                    checked={formData.consent.marketing_consent}
                                    onCheckedChange={(checked) => updateFormData('consent', 'marketing_consent', checked)}
                                    data-testid="marketing-consent-checkbox"
                                />
                                <Label htmlFor="marketing_consent" className="text-sm leading-relaxed cursor-pointer">
                                    I'm happy for GeekTickets and their service providers to contact me by call or text 
                                    (automated or not) to help support my events and occasionally share useful updates or marketing content.
                                </Label>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="promo_code">Promo Code (optional)</Label>
                            <Input
                                id="promo_code"
                                placeholder="Enter promo code if you have one"
                                value={formData.consent.promo_code}
                                onChange={(e) => updateFormData('consent', 'promo_code', e.target.value.toUpperCase())}
                                className="bg-muted/50"
                                data-testid="promo-code-input"
                            />
                        </div>
                        <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
                            <p className="text-sm text-muted-foreground">
                                By clicking "Submit Application", you agree that all information provided will be handled 
                                in full compliance with the European Union General Data Protection Regulation (GDPR).
                            </p>
                        </div>
                        <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
                            <p className="text-sm text-blue-400">
                                <strong>Note:</strong> After your application is approved, you'll be guided through a secure 
                                verification process with RyftPay where you can provide your banking details for payouts.
                            </p>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="min-h-screen bg-background flex flex-col">
            {/* Header */}
            <header className="p-6 border-b border-border">
                <div className="max-w-2xl mx-auto flex items-center justify-between">
                    <a href="https://geektickets.ie" className="flex items-center gap-2">
                        <img src={LOGO_URL} alt="GeekTickets" className="h-10" />
                    </a>
                    <span className="text-sm text-muted-foreground">Organiser Onboarding</span>
                </div>
            </header>

            {/* Stepper */}
            <div className="py-6 px-6 border-b border-border bg-muted/20">
                <div className="max-w-2xl mx-auto">
                    <div className="flex items-center justify-between">
                        {STEPS.map((step, index) => (
                            <div key={step.id} className="flex items-center">
                                <div className={`flex items-center justify-center w-10 h-10 rounded-full transition-all ${
                                    index < currentStep 
                                        ? 'bg-primary text-primary-foreground' 
                                        : index === currentStep
                                            ? 'bg-primary text-primary-foreground ring-4 ring-primary/20'
                                            : 'bg-muted text-muted-foreground'
                                }`}>
                                    {index < currentStep ? (
                                        <CheckCircle className="w-5 h-5" />
                                    ) : (
                                        <step.icon className="w-5 h-5" />
                                    )}
                                </div>
                                {index < STEPS.length - 1 && (
                                    <div className={`w-12 md:w-20 h-1 mx-2 rounded ${
                                        index < currentStep ? 'bg-primary' : 'bg-muted'
                                    }`} />
                                )}
                            </div>
                        ))}
                    </div>
                    <div className="flex justify-between mt-2">
                        {STEPS.map((step, index) => (
                            <span 
                                key={step.id} 
                                className={`text-xs ${index === currentStep ? 'text-primary' : 'text-muted-foreground'}`}
                            >
                                {step.label}
                            </span>
                        ))}
                    </div>
                </div>
            </div>

            {/* Form Content */}
            <main className="flex-1 py-8 px-6">
                <div className="max-w-2xl mx-auto">
                    <Card className="bg-card border-border">
                        <CardHeader>
                            <CardTitle className="font-['Outfit']">{STEPS[currentStep].label} Information</CardTitle>
                            <CardDescription>
                                Step {currentStep + 1} of {STEPS.length}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {error && (
                                <Alert variant="destructive" className="mb-6" data-testid="onboarding-error">
                                    <AlertCircle className="h-4 w-4" />
                                    <AlertDescription>{error}</AlertDescription>
                                </Alert>
                            )}

                            {renderStepContent()}

                            <div className="flex justify-between mt-8 pt-6 border-t border-border">
                                <Button
                                    variant="outline"
                                    onClick={handleBack}
                                    disabled={currentStep === 0 || loading}
                                    data-testid="onboarding-back-btn"
                                >
                                    Back
                                </Button>
                                {currentStep < STEPS.length - 1 ? (
                                    <Button 
                                        onClick={handleNext}
                                        className="btn-glow"
                                        data-testid="onboarding-next-btn"
                                    >
                                        Next
                                    </Button>
                                ) : (
                                    <Button 
                                        onClick={handleSubmit}
                                        className="btn-glow"
                                        disabled={loading}
                                        data-testid="onboarding-submit-btn"
                                    >
                                        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                        {loading ? 'Submitting...' : 'Submit Application'}
                                    </Button>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </main>
        </div>
    );
}
